/**
 * LUMA — beauty / skincare pop-up (demo brand).
 * Jars, tubes, droppers. Soft-luxe palette with a vibrant accent.
 */
export const brand = {
  name: 'LUMA',
  industry: 'beauty',
  template: 'editorial',
  tagline: 'Skin, lit from within.',
  drop: { label: 'The Glow Drop', endsInHours: 72 },
  // Elegant beauty identity: modern serif display, warm aubergine + rose + gold.
  theme: {
    display: "'Fraunces', Georgia, serif",
    body: "'Inter', system-ui, sans-serif",
    ink: '#2e2430',      // deep aubergine (editorial uses this as the dark stage bg)
    paper: '#f7f0ea',    // warm cream
    a1: '#d98a9a',       // rose
    a2: '#c9a24b',       // gold
    a3: '#9aae9a',       // sage
  },
  // Soft, slow, glassy — beauty motion is calm and premium.
  look: {
    exposure: 1.05, envIntensity: 1.0,
    bloomStrength: 0.5, bloomRadius: 0.8, bloomThreshold: 0.6,
    fogColor: '#2e2430', fogDensity: 0.05,
    rimLightColor: '#d98a9a', rimLightIntensity: 1.2,
    keyLightColor: '#fff4ea',
  },
  hero: {
    eyebrow: 'Limited · The Glow Drop',
    headline: 'Skin,\nlit <em>within.</em>',
    lede: 'Five hero formulas. A 72-hour pop-up. When the timer hits zero, the drop is gone.',
    cta: 'Shop the drop',
  },
  manifesto: {
    title: 'Clean.\nGlowy.\nGone soon.',
    body: 'Small batches, big glow. Dermatologist-tested, fragrance-optional, never tested on anyone but us.',
  },
  // REAL fulfillment: white-label cosmetics, no inventory, Shopify app.
  // Each product below maps to a Blanka catalog item — rebrand + sell.
  fulfillment: {
    partner: 'Blanka',
    url: 'https://blanka.com',
    model: 'White-label beauty · no MOQ · print-on-demand · made US/CA/KR',
    cost: '~$39/mo',
    margin: '40–65%',
  },
};

// Each product maps to a real Blanka catalog item (source). Prices are
// realistic DTC retail; Blanka wholesale runs roughly 1/3 of these.
export const products = [
  {
    id: 'serum', name: 'HALO SERUM', tag: 'Vitamin C Serum · 30ml', price: '$34',
    source: 'Blanka — Vitamin C Brightening Serum',
    // REAL Blender-modeled glass dropper (recipes/product-render.py).
    model: '/models/halo-serum.glb', image: '/renders/halo-serum.png', keepMaterials: true, modelScale: 2.2,
    shape: 'tube', framing: 'tall', material: 'glass',
    colorA: '#ffb347', colorB: '#fff3e0', accent: '#ff5e8a', mood: 'sun',
    variants: [
      { sku: 'HALO-C',   name: 'Vit C',       colorA: '#ffb347', colorB: '#fff3e0', accent: '#ff5e8a', price: '$34' },
      { sku: 'HALO-NIA', name: 'Niacinamide', colorA: '#b8e0d2', colorB: '#fff',     accent: '#ff5e8a', price: '$34' },
    ],
  },
  {
    id: 'cream', name: 'CLOUD CREAM', tag: 'Hydra Moisturizer · 50ml', price: '$32',
    source: 'Blanka — Hydrating Face Moisturizer',
    model: '/models/cloud-cream.glb', image: '/renders/cloud-cream.png', keepMaterials: true, modelScale: 2.0,
    shape: 'jar', framing: 'square', material: 'porcelain',
    colorA: '#f5e0e8', colorB: '#e8b4b8', accent: '#c9184a', mood: 'pop',
  },
  {
    id: 'lip', name: 'DEW OIL', tag: 'Tinted Lip Oil · 8ml', price: '$18',
    source: 'Blanka — Lip Oil / Gloss',
    model: '/models/dew-oil.glb', image: '/renders/dew-oil.png', keepMaterials: true, modelScale: 2.0,
    shape: 'tube', framing: 'tall', material: 'plastic',
    colorA: '#ff5e8a', colorB: '#0a0a0a', accent: '#ffd700', mood: 'sun',
    variants: [
      { sku: 'DEW-ROSE',  name: 'Rose',  colorA: '#ff5e8a', colorB: '#0a0a0a', accent: '#ffd700', price: '$18' },
      { sku: 'DEW-PEACH', name: 'Peach', colorA: '#ffaa7a', colorB: '#0a0a0a', accent: '#ffd700', price: '$18' },
      { sku: 'DEW-PLUM',  name: 'Plum',  colorA: '#8b3a62', colorB: '#fff',     accent: '#ffd700', price: '$18' },
    ],
  },
  {
    id: 'mist', name: 'AURA MIST', tag: 'Rose Facial Mist · 100ml', price: '$24',
    source: 'Blanka — Rosewater Facial Toner/Mist',
    model: '/models/aura-mist.glb', image: '/renders/aura-mist.png', keepMaterials: true, modelScale: 2.3,
    shape: 'bottle', framing: 'tall', material: 'glass',
    colorA: '#c8a2c8', colorB: '#fff', accent: '#39d0ff', mood: 'storm',
  },
  {
    id: 'mask', name: 'MIDNIGHT MASK', tag: 'Overnight Cream · 60ml', price: '$38',
    source: 'Blanka — Night Cream / Overnight Mask',
    model: '/models/midnight-mask.glb', image: '/renders/midnight-mask.png', keepMaterials: true, modelScale: 2.0,
    shape: 'jar', framing: 'square', material: 'chrome',
    colorA: '#1a1a2e', colorB: '#3a3a5e', accent: '#bf00ff', mood: 'midnight',
  },
];

export const moodTints = {
  pop: '#fff5f7', sun: '#fff4e6', storm: '#f3edff', mint: '#eafff6', midnight: '#16121f',
};
